import { FieldPolicy } from '@apollo/client';
import _uniqBy from 'lodash/uniqBy';

// Not sure how to type this correctly without using <any>
type KeyArgs = FieldPolicy<any>['keyArgs']; // eslint-disable-line

/**
 * Provides a FieldPolicy that implements pagination for AppSync requests, which return data
 * including a `nextToken` for making paginated requests and an `items` array of the current page of
 * results.
 *
 * For an example implementation, see: packages/web/src/components/pages/Tracks/Tracks.tsx
 *
 * When using this function, the type you give it to fill the generic TNode type is the type of the
 * elements in the `items` list, for example, `TrqkSong` is the TNode for the `getTrqkSongsForUser`
 * query.
 *
 * As of 8/18/20, pagination using FieldPolicy is under active development in Apollo Client v3 and
 * there are open issues on the subject.
 * @param keyArgs see @apollo/client docs on how KeyArgs work in a FieldPolicy
 */
export function appSyncPagination<TNode>(
    keyArgs: KeyArgs = false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Not sure how to type this correctly without using <any>
): FieldPolicy<any> {
    // eslint-disable-line
    return {
        keyArgs,
        read: (
            existing: Readonly<AppSyncConnection<TNode>>,
        ): void | Readonly<AppSyncConnection<TNode>> => {
            if (!existing) {
                return undefined;
            }

            return existing;
        },
        merge: (
            existing: Readonly<AppSyncConnection<TNode>>,
            incoming: Readonly<AppSyncConnection<TNode>>,
        ): Readonly<AppSyncConnection<TNode>> => {
            if (!existing) {
                return incoming;
            }

            return {
                __typename: incoming.__typename,
                // Using _uniqBy() here prevents duplicate items when we use a `fetchPolicy` and/or
                // `nextFetchPolicy` other than 'network-only'
                items: _uniqBy([...existing.items, ...incoming.items], 'ref'),
                nextToken: incoming?.nextToken ?? null,
            };
        },
    };
}
